import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';

// ─────────────────────────────────────────────────────────────
//  Página pública (sin login): la familia abre el enlace de pago de su reserva
//  nativa (?token=…) y paga el importe pendiente con Stripe. Funciona en paralelo
//  al flujo /completar de las reservas web.
// ─────────────────────────────────────────────────────────────

export default function PagarReserva() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  const pagoQuery = params.get('pago'); // 'ok' | 'cancelado' | null
  const [phase, setPhase] = useState('loading'); // loading | error | form | pagado
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null); // {familyName, eventName, campistas, total, paid, remaining}
  const [pagando, setPagando] = useState(false);

  const load = useCallback(async () => {
    if (!token) { setError('Enlace no válido.'); setPhase('error'); return; }
    try {
      const r = await api.getNativePayLink(token);
      setInfo(r);
      if (pagoQuery === 'ok' || r.pagado) setPhase('pagado');
      else setPhase('form');
    } catch (e) {
      setError(e.message || 'No se pudo cargar el enlace. Puede haber caducado.');
      setPhase('error');
    }
  }, [token, pagoQuery]);

  useEffect(() => { load(); }, [load]);

  const pagar = async () => {
    setPagando(true);
    try {
      const r = await api.payNativeLink(token);
      if (r.url) window.location.href = r.url;
      else { setError('No se pudo iniciar el pago.'); setPhase('error'); }
    } catch (e) {
      setError(e.message || 'No se pudo iniciar el pago.');
      setPhase('error');
      setPagando(false);
    }
  };

  const money = n => `${(Number(n) || 0).toFixed(2)} €`;

  const wrap = (children) => (
    <div style={{ minHeight: '100vh', background: '#1a1612', color: '#f2ead8', fontFamily: 'Georgia, serif', padding: '2rem 1rem' }}>
      <div style={{ maxWidth: 540, margin: '0 auto' }}>
        <h1 style={{ color: '#c8820a', fontSize: '1.5rem', marginBottom: '.2rem' }}>💳 Pago de tu reserva</h1>
        <p style={{ color: '#8a8070', fontSize: '.85rem', marginTop: 0, marginBottom: '1.8rem' }}>Aldealix la Aldea Celta</p>
        {children}
      </div>
    </div>
  );

  if (phase === 'loading') return wrap(<p style={{ color: '#b0a898' }}>Cargando…</p>);

  if (phase === 'error') return wrap(
    <div style={{ background: '#221e19', borderLeft: '3px solid #b5482f', padding: 20, borderRadius: 2 }}>
      <p style={{ margin: 0, color: '#f2ead8' }}>⚠️ {error}</p>
    </div>
  );

  if (phase === 'pagado') return wrap(
    <div style={{ background: '#221e19', borderLeft: '3px solid #5a7a50', padding: 24, borderRadius: 2 }}>
      <p style={{ margin: 0, color: '#f2ead8', fontSize: '1.05rem' }}>✅ ¡Pago completado! Gracias.</p>
      <p style={{ color: '#b0a898', lineHeight: 1.7, marginBottom: 0 }}>Tu reserva{info?.eventName ? ` de ${info.eventName}` : ''} queda al corriente de pago.</p>
    </div>
  );

  // phase === 'form'
  return wrap(
    <>
      {pagoQuery === 'cancelado' && (
        <div style={{ background: '#221e19', borderLeft: '3px solid #c8820a', padding: 14, borderRadius: 2, marginBottom: 16, color: '#b0a898', fontSize: '.85rem' }}>
          El pago se canceló. Puedes intentarlo de nuevo cuando quieras.
        </div>
      )}
      <p style={{ color: '#b0a898', lineHeight: 1.7 }}>
        {info?.familyName ? <>Hola, <strong style={{ color: '#f2ead8' }}>{info.familyName}</strong>: </> : null}
        este es el importe pendiente de tu reserva{info?.eventName ? <> de <strong style={{ color: '#f2ead8' }}>{info.eventName}</strong></> : null}.
      </p>

      {Array.isArray(info?.campistas) && info.campistas.length > 0 && (
        <div style={{ background: '#221e19', borderLeft: '3px solid #5a7a50', padding: '12px 16px', borderRadius: 2, margin: '14px 0', color: '#b0a898', fontSize: '.85rem' }}>
          🧒 {info.campistas.join(' · ')}
        </div>
      )}

      <div style={{ background: '#221e19', borderLeft: '3px solid #c8820a', padding: '16px 20px', borderRadius: 2, margin: '18px 0' }}>
        <p style={{ margin: '0 0 6px', fontSize: 13, color: '#8a8070' }}>IMPORTE A PAGAR</p>
        <p style={{ margin: 0, color: '#f2ead8', fontSize: 24, fontWeight: 700 }}>{money(info?.remaining)}</p>
        {Number(info?.paid) > 0 && (
          <p style={{ margin: '6px 0 0', fontSize: 12, color: '#8a8070' }}>Total {money(info?.total)} · ya pagado {money(info?.paid)}</p>
        )}
      </div>

      <button onClick={pagar} disabled={pagando}
        style={{ display: 'block', width: '100%', textAlign: 'center', background: '#c8820a', color: '#1a1612', padding: 14, borderRadius: 6, border: 'none', fontWeight: 700, fontSize: '1rem', cursor: pagando ? 'default' : 'pointer', opacity: pagando ? 0.7 : 1, fontFamily: 'inherit' }}>
        {pagando ? 'Redirigiendo al pago…' : `Pagar ${money(info?.remaining)}`}
      </button>
      <p style={{ color: '#8a8070', fontSize: '.78rem', marginTop: '1rem', lineHeight: 1.6 }}>
        Pago seguro con tarjeta (Stripe). Serás redirigido a la pasarela de pago.
      </p>
    </>
  );
}
